import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Building2 } from 'lucide-react';

// Settings → Property Management → OTA-based single-property onboarding.
// Only rendered in All Properties mode (admin). Additive; never triggers any
// OTA push/ARI — the OTA APIs are called only to fetch the property's listing.
export const OtaPropertyOnboarding = ({ apiBase, onCreated }) => {
  const [mmtOpen, setMmtOpen] = useState(false);
  const [agodaOpen, setAgodaOpen] = useState(false);
  const [yatraOpen, setYatraOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [mmt, setMmt] = useState({ hotel_name: '', hotel_code: '', bearer_token: '' });
  const [agoda, setAgoda] = useState({ hotel_name: '', hotel_code: '' });
  const [yatra, setYatra] = useState({ hotel_name: '', hotel_code: '' });

  const authHdr = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };


  const onSelectOta = (val) => {
    if (val === 'mmt') { setMmt({ hotel_name: '', hotel_code: '', bearer_token: '' }); setMmtOpen(true); }
    else if (val === 'agoda') { setAgoda({ hotel_name: '', hotel_code: '' }); setAgodaOpen(true); }
    else if (val === 'yatra') { setYatra({ hotel_name: '', hotel_code: '' }); setYatraOpen(true); }
  };

  const submitMmt = async () => {
    if (!mmt.hotel_name.trim() || !mmt.hotel_code.trim() || !mmt.bearer_token.trim()) {
      toast.error('Hotel Name, Hotel Code and Bearer Token are all required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${apiBase}/onboarding/mmt`, {
        hotel_name: mmt.hotel_name.trim(),
        hotel_code: mmt.hotel_code.trim(),
        bearer_token: mmt.bearer_token,
      }, authHdr);
      toast.success(`MMT property created successfully — ${res.data.room_types_count} room types, ${res.data.rate_plans_count} rate plans`);
      setMmtOpen(false);
      setMmt({ hotel_name: '', hotel_code: '', bearer_token: '' });
      if (onCreated) await onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unable to create MMT property');
    } finally {
      setSubmitting(false);
    }
  };

  const submitAgoda = async () => {
    if (!agoda.hotel_name.trim() || !agoda.hotel_code.trim()) {
      toast.error('Hotel Name and Hotel Code are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${apiBase}/onboarding/agoda`, {
        hotel_name: agoda.hotel_name.trim(),
        hotel_code: agoda.hotel_code.trim(),
      }, authHdr);
      toast.success(`Agoda property created successfully — ${res.data.room_types_count} room types, ${res.data.rate_plans_count} rate plans`);
      setAgodaOpen(false);
      setAgoda({ hotel_name: '', hotel_code: '' });
      if (onCreated) await onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unable to create Agoda property');
    } finally {
      setSubmitting(false);
    }
  };

  const submitYatra = async () => {
    if (!yatra.hotel_name.trim() || !yatra.hotel_code.trim()) {
      toast.error('Hotel Name and Hotel Code are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${apiBase}/onboarding/yatra`, {
        hotel_name: yatra.hotel_name.trim(),
        hotel_code: yatra.hotel_code.trim(),
      }, authHdr);
      toast.success(`Yatra property created — ${res.data.room_types_count} room types & ${res.data.rate_plans_count} rate plans mapped and saved automatically`);
      setYatraOpen(false);
      setYatra({ hotel_name: '', hotel_code: '' });
      if (onCreated) await onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unable to create Yatra property');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-3 border-t border-gray-100" data-testid="ota-onboarding-section">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <Label className="text-sm font-medium text-gray-700 flex items-center gap-1">
          <Building2 className="w-4 h-4 text-orange-600" /> Add Property via OTA
        </Label>
        <Select value="" onValueChange={onSelectOta}>
          <SelectTrigger className="w-52" data-testid="onboarding-ota-select">
            <SelectValue placeholder="Select OTA" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mmt" data-testid="onboarding-ota-mmt">MMT</SelectItem>
            <SelectItem value="agoda" data-testid="onboarding-ota-agoda">Agoda</SelectItem>
            <SelectItem value="yatra" data-testid="onboarding-ota-yatra">Yatra</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[11px] text-gray-500">Fetches the property listing from the OTA and creates it here. No rates/inventory are pushed to the OTA.</span>
      </div>

      {/* MMT modal */}
      <Dialog open={mmtOpen} onOpenChange={(o) => { if (!submitting) setMmtOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="mmt-onboard-modal">
          <DialogHeader><DialogTitle>Create MMT Property</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Hotel Name</Label>
              <Input value={mmt.hotel_name} onChange={(e) => setMmt({ ...mmt, hotel_name: e.target.value })}
                disabled={submitting} data-testid="mmt-hotel-name" />
            </div>
            <div>
              <Label className="text-xs">Hotel Code</Label>
              <Input value={mmt.hotel_code} onChange={(e) => setMmt({ ...mmt, hotel_code: e.target.value })}
                disabled={submitting} data-testid="mmt-hotel-code" />
            </div>
            <div>
              <Label className="text-xs">Bearer Token</Label>
              <Input type="password" autoComplete="off" value={mmt.bearer_token}
                onChange={(e) => setMmt({ ...mmt, bearer_token: e.target.value })}
                disabled={submitting} data-testid="mmt-bearer-token" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMmtOpen(false)} disabled={submitting} data-testid="mmt-cancel-btn">Cancel</Button>
            <Button onClick={submitMmt} disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-amber-600 text-white" data-testid="mmt-save-btn">
              {submitting ? 'Fetching property details…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agoda modal */}
      <Dialog open={agodaOpen} onOpenChange={(o) => { if (!submitting) setAgodaOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="agoda-onboard-modal">
          <DialogHeader><DialogTitle>Create Agoda Property</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Hotel Name</Label>
              <Input value={agoda.hotel_name} onChange={(e) => setAgoda({ ...agoda, hotel_name: e.target.value })}
                disabled={submitting} data-testid="agoda-hotel-name" />
            </div>
            <div>
              <Label className="text-xs">Hotel Code</Label>
              <Input value={agoda.hotel_code} onChange={(e) => setAgoda({ ...agoda, hotel_code: e.target.value })}
                disabled={submitting} data-testid="agoda-hotel-code" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAgodaOpen(false)} disabled={submitting} data-testid="agoda-cancel-btn">Cancel</Button>
            <Button onClick={submitAgoda} disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-amber-600 text-white" data-testid="agoda-save-btn">
              {submitting ? 'Fetching property details…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yatra modal */}
      <Dialog open={yatraOpen} onOpenChange={(o) => { if (!submitting) setYatraOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="yatra-onboard-modal">
          <DialogHeader><DialogTitle>Create Yatra Property</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Hotel Name</Label>
              <Input value={yatra.hotel_name} onChange={(e) => setYatra({ ...yatra, hotel_name: e.target.value })}
                disabled={submitting} data-testid="yatra-hotel-name" />
            </div>
            <div>
              <Label className="text-xs">Hotel Code</Label>
              <Input value={yatra.hotel_code} onChange={(e) => setYatra({ ...yatra, hotel_code: e.target.value })}
                disabled={submitting} data-testid="yatra-hotel-code" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setYatraOpen(false)} disabled={submitting} data-testid="yatra-cancel-btn">Cancel</Button>
            <Button onClick={submitYatra} disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-amber-600 text-white" data-testid="yatra-save-btn">
              {submitting ? 'Fetching property details…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OtaPropertyOnboarding;
